import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getOne, list } from "@/lib/api";
import { ArrowLeft, Phone, MessageCircle, Clock, MapPin, User } from "lucide-react";

const waLink = (phone) => `https://wa.me/${String(phone || "").replace(/[^0-9]/g, "")}`;

export default function EventDayMode() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [rundown, setRundown] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [client, setClient] = useState(null);
  const [now, setNow] = useState(new Date());

  useEffect(() => {
    getOne("events", id).then((ev) => { setEvent(ev); if (ev?.client_id) getOne("clients", ev.client_id).then(setClient).catch(() => {}); }).catch(() => {});
    list("rundown", { event_id: id }).then((d) => setRundown(d.sort((a, b) => String(a.time).localeCompare(String(b.time))))).catch(() => {});
    list("event_vendors", { event_id: id }).then(setVendors).catch(() => {});
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, [id]);

  const { current, next, countdown } = useMemo(() => {
    const mins = now.getHours() * 60 + now.getMinutes();
    const toMin = (t) => { const [h, m] = String(t || "0:0").split(":").map(Number); return (h || 0) * 60 + (m || 0); };
    let current = null, next = null;
    for (let i = 0; i < rundown.length; i++) {
      if (toMin(rundown[i].time) <= mins) current = rundown[i];
      else { next = rundown[i]; break; }
    }
    let countdown = null;
    if (next) {
      const diff = toMin(next.time) * 60 - (now.getHours() * 3600 + now.getMinutes() * 60 + now.getSeconds());
      if (diff > 0) { const h = Math.floor(diff / 3600), m = Math.floor((diff % 3600) / 60), s = diff % 60; countdown = `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`; }
    }
    return { current, next, countdown };
  }, [now, rundown]);

  const contacts = [
    ...(client ? [{ label: "Client", name: `${client.groom} & ${client.bride}`, phone: client.phone }] : []),
    ...vendors.map((v) => ({ label: v.vendor_name, name: v.vendor_name, phone: "" })),
  ];

  if (!event) return <div className="min-h-screen bg-[#1a0409] text-white flex items-center justify-center">Memuat...</div>;

  return (
    <div className="min-h-screen bg-[#1a0409] text-white">
      <div className="max-w-4xl mx-auto px-5 py-6">
        <button onClick={() => navigate(`/events/${id}`)} className="flex items-center gap-1 text-sm text-gold-light hover:text-white mb-6" data-testid="daymode-back"><ArrowLeft className="w-4 h-4" />Keluar Event Day Mode</button>

        <div className="text-center mb-8">
          <p className="text-[10px] uppercase tracking-[0.3em] text-gold-light">Event Day Mode</p>
          <h1 className="font-heading text-4xl">{event.groom} & {event.bride}</h1>
          <p className="font-mono text-6xl sm:text-7xl mt-4 tabular-nums tracking-tight" data-testid="daymode-clock">
            {now.toLocaleTimeString("id-ID", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
          <div className="rounded-2xl bg-white/10 backdrop-blur p-6 border border-gold/20" data-testid="daymode-now">
            <p className="text-xs uppercase tracking-[0.2em] text-gold-light mb-2">Now</p>
            {current ? (
              <>
                <p className="font-mono text-2xl text-gold-light">{current.time}</p>
                <h2 className="font-heading text-3xl mt-1">{current.activity}</h2>
                <div className="flex flex-wrap gap-x-4 gap-y-1 mt-3 text-sm text-white/70">
                  {current.pic && <span className="flex items-center gap-1"><User className="w-4 h-4" />{current.pic}</span>}
                  {current.location && <span className="flex items-center gap-1"><MapPin className="w-4 h-4" />{current.location}</span>}
                </div>
              </>
            ) : <p className="text-white/60 mt-3">Belum ada aktivitas berjalan.</p>}
          </div>

          <div className="rounded-2xl bg-gold/15 backdrop-blur p-6 border border-gold/30" data-testid="daymode-next">
            <p className="text-xs uppercase tracking-[0.2em] text-gold-light mb-2">Next</p>
            {next ? (
              <>
                <p className="font-mono text-2xl text-gold-light">{next.time}</p>
                <h2 className="font-heading text-3xl mt-1">{next.activity}</h2>
                {countdown && <p className="font-mono text-4xl mt-3 text-gold-light tabular-nums" data-testid="daymode-countdown">{countdown}</p>}
              </>
            ) : <p className="text-white/60 mt-3">Tidak ada aktivitas berikutnya.</p>}
          </div>
        </div>

        <div className="mb-6">
          <p className="text-xs uppercase tracking-[0.2em] text-gold-light mb-3 flex items-center gap-2"><Clock className="w-4 h-4" />Full Rundown</p>
          <div className="space-y-2">
            {rundown.map((r) => (
              <div key={r.id} className={`flex items-center gap-4 rounded-xl px-4 py-2.5 ${current?.id === r.id ? "bg-gold/25 border border-gold/40" : "bg-white/5"}`}>
                <span className="font-mono text-sm text-gold-light w-14">{r.time}</span>
                <span className="flex-1 text-sm">{r.activity}</span>
                <span className="text-xs text-white/50">{r.pic}</span>
              </div>
            ))}
          </div>
        </div>

        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-gold-light mb-3">Emergency Contacts</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {contacts.map((c, i) => (
              <div key={i} className="flex items-center justify-between rounded-xl bg-white/5 px-4 py-3" data-testid="emergency-contact">
                <div><p className="text-sm font-semibold">{c.name}</p><p className="text-xs text-white/50">{c.label}</p></div>
                {c.phone && (
                  <div className="flex gap-2">
                    <a href={`tel:${c.phone}`} aria-label="Call" className="w-9 h-9 rounded-full bg-burgundy flex items-center justify-center" data-testid="emergency-call"><Phone className="w-4 h-4" /></a>
                    <a href={waLink(c.phone)} target="_blank" rel="noreferrer" aria-label="WhatsApp" className="w-9 h-9 rounded-full bg-emerald-600 flex items-center justify-center" data-testid="emergency-wa"><MessageCircle className="w-4 h-4" /></a>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
